import React, {useEffect, useState, useRef} from 'react';
import {
    Megaphone,
    Zap,
    Users,
    UserCheck,
    Gift,
    Loader2,
    CheckCircle2,
    ChevronLeft,
    Send,
    ImagePlus,
    X,
    Pencil,
    Shell
} from 'lucide-react';
import {messageGeneration, messageSend} from "../../api/ai/ai.api";
import {useRecoilValue} from "recoil";
import {accountSelector, channelSelector} from "../../store/selector/selector";
import {Dropdown} from "omoplay";

const AiMessageGenerator = () => {
    const account = useRecoilValue(accountSelector)
    const channelId = useRecoilValue(channelSelector)

    // `GET /account` 가 실패하면 account 는 `{}` 다. 무방비로 `account.channels.length` 를 읽으면
    // 렌더 중 TypeError 가 나고, 이 앱에는 ErrorBoundary 가 없어 **앱 전체가 흰 화면**이 된다
    // (2026-09-14 관리자 세션 전환 신고. 상세 = `Layout.js` 상단 주석).
    const channels = account?.channels ?? []

    const textareaRef = useRef(null);

    const [step, setStep] = useState(1);
    const [formData, setFormData] = useState({
        type: '',
        target: '',
        benefit: '',
        image: null
    });

    const [imagePreview, setImagePreview] = useState(null);
    const [result, setResult] = useState(null);

    const [editableData, setEditableData] = useState({});

    // --- [변경 1] Step 5에서 새로고침/이탈 방지 경고창 로직 ---
    useEffect(() => {
        const handleBeforeUnload = (e) => {
            if (step === 5) {
                // 표준 브라우저 경고창 트리거
                e.preventDefault();
                e.returnValue = ''; // Chrome 등 모던 브라우저 필수 설정
            }
        };

        if (step === 5) {
            window.addEventListener('beforeunload', handleBeforeUnload);
        }

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [step]);

    // 기존 Textarea 높이 조절 로직
    useEffect(() => {
        if (step === 5 && textareaRef.current) {
            textareaRef.current.style.height = 'auto';
            textareaRef.current.style.height = textareaRef.current.scrollHeight + 'px';
        }
    }, [step, editableData.content]);

    const progress = (step / 5) * 100;

    const handleSelect = (key, value) => {
        setFormData({ ...formData, [key]: value });
        setTimeout(() => setStep(prev => prev + 1), 200);
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setFormData({ ...formData, image: file });
            setImagePreview(URL.createObjectURL(file));
        }
    };

    const removeImage = () => {
        setFormData({ ...formData, image: null });
        setImagePreview(null);
    };

    const generateMessage = () => {
        if (!formData.benefit) return alert('혜택 내용을 입력해주세요!');

        setStep(4);

        messageGeneration(formData.type, formData.target, formData.benefit, formData.image).then(({data}) => {
            setStep(5);
            setResult({
                imageUrl: data.imageUrl,
                messageId: data.messageId,
            });
            setEditableData({
                content: data.content,
                couponTitle: '직원 확인',
                couponDescription: '직원 확인',
            });
        })
    };

    const handleFinalSend = () => {
        if (!window.confirm("정말 발송하시겠습니까? 전송에는 약 5분이 걸릴 수 있습니다.")) {
            return; // 취소 누르면 함수 종료
        }

        messageSend(editableData.content, editableData.couponTitle, editableData.couponDescription, formData.target, result.messageId).then(() => {
            alert("성공")
        })
    };

    return (
        <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center font-sans">
            <div className="w-full max-w-md bg-white min-h-screen shadow-xl overflow-hidden flex flex-col relative">
                {/* 상단 헤더 & 진행바 */}
                <div className="px-6 pt-8 pb-4 bg-white z-20 sticky top-0 shadow-sm">
                    <button
                        onClick={() => window.location.href = "/volatility-message"}
                        className="w-full p-2 border-2 border-gray-100 rounded-2xl hover:border-indigo-500 hover:bg-indigo-50 transition-all flex items-center text-left group"
                    >
                        <div className="w-7 h-7 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center mr-5 group-hover:scale-110 transition-transform">
                            <Shell size={14} />
                        </div>
                        <div>
                            <h3 className="text-lg font-bold text-gray-500">관리자 페이지로 돌아가기</h3>
                        </div>
                    </button>
                    <div className="mt-2 mb-4">
                        <Dropdown variants="default"
                                  showSearch={true}
                                  titleStyle={{backgroundColor: "#FFF", color: "black", borderRadius: "8px"}}
                                  boxStyle={{overflow: "scroll", color: "black"}}
                                  initialValue={channelId}
                                  items={channels.length === 0 ? [{}] : channels.map(channel => {
                                      return ({
                                          label: channel.name,
                                          value: channel.id
                                      })
                                  })}
                                  onChanged={(items) => {
                                      if (channelId !== Number(items[0].value)) {
                                          localStorage.setItem("channelState", items[0].value)
                                          window.location.reload()
                                      }
                                  }} />
                    </div>

                    <div className="flex justify-between items-center mb-4">
                        {step > 1 && step < 5 ? (
                            <button onClick={() => setStep(step - 1)} className="text-gray-400 hover:text-gray-600">
                                <ChevronLeft size={28} />
                            </button>
                        ) : <div className="w-7"></div>}
                        <span className="text-sm font-bold text-indigo-600 tracking-widest">AI MESSAGE BOT</span>
                        <div className="w-7"></div>
                    </div>
                    <div className="w-full bg-gray-100 h-2 rounded-full overflow-hidden">
                        <div
                            className="h-full bg-indigo-600 transition-all duration-500 ease-out"
                            style={{ width: `${progress}%` }}
                        ></div>
                    </div>
                </div>

                {/* 메인 컨텐츠 영역 */}
                <div className="flex-1 px-6 pb-10 flex flex-col justify-center animate-fadeIn">

                    {/* STEP 1 */}
                    {step === 1 && (
                        <div className="space-y-6">
                            <div className="text-center mb-8">
                                <h2 className="text-2xl font-bold text-gray-900 mb-2">어떤 메시지를 보낼까요?</h2>
                                <p className="text-gray-500">가장 효과적인 테마를 골라주세요.</p>
                            </div>

                            <button
                                onClick={() => handleSelect('type', '이벤트/쿠폰 홍보')}
                                className="w-full p-6 border-2 border-gray-100 rounded-2xl hover:border-indigo-500 hover:bg-indigo-50 transition-all flex items-center text-left group"
                            >
                                <div className="w-14 h-14 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center mr-5 group-hover:scale-110 transition-transform">
                                    <Megaphone size={28} />
                                </div>
                                <div>
                                    <h3 className="text-lg font-bold text-gray-800">이벤트/쿠폰 홍보</h3>
                                    <p className="text-sm text-gray-500 mt-1">신메뉴 알림, 정기 이벤트 등</p>
                                </div>
                            </button>

                            <button
                                onClick={() => handleSelect('type', '오늘만 할인(재고 소진)')}
                                className="w-full p-6 border-2 border-gray-100 rounded-2xl hover:border-red-500 hover:bg-red-50 transition-all flex items-center text-left group"
                            >
                                <div className="w-14 h-14 bg-red-100 text-red-600 rounded-full flex items-center justify-center mr-5 group-hover:scale-110 transition-transform">
                                    <Zap size={28} />
                                </div>
                                <div>
                                    <h3 className="text-lg font-bold text-gray-800">긴급 타임 세일</h3>
                                    <p className="text-sm text-gray-500 mt-1">오늘만 할인, 재고 소진 임박</p>
                                </div>
                            </button>
                        </div>
                    )}

                    {/* STEP 2 */}
                    {step === 2 && (
                        <div className="space-y-6">
                            <div className="text-center mb-8">
                                <h2 className="text-2xl font-bold text-gray-900 mb-2">누구에게 보낼까요?</h2>
                                <p className="text-gray-500">타겟을 좁히면 비용이 절약됩니다.</p>
                            </div>

                            <button
                                onClick={() => handleSelect('target', 'all')}
                                className="w-full p-6 border-2 border-gray-100 rounded-2xl hover:border-indigo-500 hover:bg-indigo-50 transition-all flex items-center text-left opacity-70 hover:opacity-100"
                            >
                                <div className="w-12 h-12 bg-gray-100 text-gray-600 rounded-full flex items-center justify-center mr-5">
                                    <Users size={24} />
                                </div>
                                <div>
                                    <h3 className="text-lg font-bold text-gray-800">전체 고객</h3>
                                    <p className="text-sm text-gray-500 mt-1">모든 방문 고객 대상</p>
                                </div>
                            </button>

                            <button
                                onClick={() => handleSelect('target', 'filter')}
                                className="w-full p-6 border-2 border-indigo-500 bg-indigo-50 rounded-2xl relative shadow-md transform scale-105 transition-all flex items-center text-left"
                            >
                                <div className="absolute -top-3 right-4 bg-red-500 text-white text-xs font-bold px-3 py-1 rounded-full shadow-sm">
                                    비용 40% 절감 추천!
                                </div>
                                <div className="w-12 h-12 bg-indigo-100 text-indigo-600 rounded-full flex items-center justify-center mr-5">
                                    <UserCheck size={24} />
                                </div>
                                <div>
                                    <h3 className="text-lg font-bold text-indigo-900">우리 가게 단골만</h3>
                                    <p className="text-sm text-indigo-600 mt-1">1회 이상 클릭한 고객 타겟</p>
                                </div>
                            </button>
                        </div>
                    )}

                    {/* STEP 3 */}
                    {step === 3 && (
                        <div className="space-y-6">
                            <div className="text-center mb-6">
                                <div className="w-16 h-16 bg-yellow-100 text-yellow-600 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <Gift size={32} />
                                </div>
                                <h2 className="text-2xl font-bold text-gray-900 mb-2">어떤 혜택을 줄까요?</h2>
                                <p className="text-gray-500 text-sm px-4">메뉴 사진이 있다면 함께 올려주세요.<br/>AI가 사진에 어울리는 이미지를 만들어 드립니다.</p>
                            </div>

                            {/* 이미지 업로드 영역 */}
                            <div>
                                <input
                                    type="file"
                                    accept="image/png, image/jpeg"
                                    onChange={handleImageChange}
                                    className="hidden"
                                    id="image-upload"
                                />

                                {!imagePreview ? (
                                    <label
                                        htmlFor="image-upload"
                                        className="w-full h-32 border-2 border-dashed border-gray-300 rounded-2xl flex flex-col items-center justify-center text-gray-400 cursor-pointer hover:border-indigo-400 hover:text-indigo-500 hover:bg-indigo-50 transition-all"
                                    >
                                        <ImagePlus size={32} className="mb-2" />
                                        <span className="text-sm font-medium">사진 추가하기 (선택)</span>
                                    </label>
                                ) : (
                                    <div className="relative w-full h-48 rounded-2xl overflow-hidden border border-gray-200 shadow-sm group">
                                        <img
                                            src={imagePreview}
                                            alt="Preview"
                                            className="w-full h-full"
                                        />
                                        <div className="absolute inset-0 bg-black/20 group-hover:bg-black/40 transition-colors"></div>
                                        <button
                                            onClick={removeImage}
                                            className="absolute top-3 right-3 bg-white/90 text-gray-700 p-2 rounded-full shadow-lg hover:bg-red-50 hover:text-red-600 transition-colors"
                                        >
                                            <X size={20} />
                                        </button>
                                    </div>
                                )}
                            </div>

                            <div className="relative">
                                <textarea
                                    className="w-full p-5 border-2 border-gray-200 rounded-2xl focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100 transition-all outline-none text-lg min-h-[140px] resize-none"
                                    placeholder="예) 2만원 이상 주문하면 감자튀김 서비스, 오늘 오면 소주 1병 공짜"
                                    value={formData.benefit}
                                    onChange={(e) => setFormData({...formData, benefit: e.target.value})}
                                ></textarea>
                            </div>

                            <button
                                onClick={generateMessage}
                                disabled={!formData.benefit}
                                className={`w-full py-4 rounded-xl font-bold text-lg shadow-lg flex items-center justify-center space-x-2 transition-all
                  ${formData.benefit
                                    ? 'bg-indigo-600 text-white hover:bg-indigo-700 transform hover:-translate-y-1'
                                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`}
                            >
                                <span>AI 메시지 생성하기</span>
                                <Send size={20} />
                            </button>
                        </div>
                    )}

                    {/* STEP 4 */}
                    {step === 4 && (
                        <div className="text-center py-20">
                            <div className="relative inline-block mb-8">
                                <div className="absolute inset-0 bg-indigo-500 opacity-20 blur-xl rounded-full animate-pulse"></div>
                                <Loader2 size={64} className="text-indigo-600 animate-spin relative z-10" />
                            </div>
                            <h3 className="text-2xl font-bold text-gray-900 mb-2">AI가 메시지를 만들고 있어요</h3>
                            <p className="text-gray-500 animate-pulse">
                                쿠폰 문구 확인 중...<br/>
                                고객의 니즈 파악 중...<br/>
                                이미지 생성 중...
                            </p>
                        </div>
                    )}

                    {/* STEP 5: 결과 및 직접 수정/전송 */}
                    {step === 5 && (
                        <div className="space-y-6">
                            <div className="text-center mb-4">
                                <div className="inline-flex items-center justify-center space-x-2 bg-green-100 text-green-700 px-4 py-1 rounded-full text-sm font-bold mb-4">
                                    <CheckCircle2 size={16} />
                                    <span>생성 완료!</span>
                                </div>
                                <h2 className="text-xl font-bold text-gray-900">내용과 버튼을 눌러 수정할 수 있어요!</h2>
                            </div>

                            {/* 카톡 미리보기 카드 (Editable) */}
                            <div className="bg-[#FAE100] p-4 rounded-2xl shadow-md mx-2">
                                <div className="bg-white rounded-xl overflow-hidden shadow-sm">
                                    {/* 이미지 영역 */}
                                    <div className={`flex items-center justify-center text-gray-400`}>
                                        <img src={result?.imageUrl} alt="" className="w-full" />
                                    </div>

                                    <div className="p-5">
                                        {/* 1. 수정 가능한 본문 영역 (높이 자동 조절) */}
                                        <div className="relative group">
                                            <textarea
                                                ref={textareaRef}
                                                maxLength={400}
                                                className="w-full h-auto min-h-[100px] text-gray-600 text-sm whitespace-pre-wrap leading-relaxed bg-transparent border border-transparent rounded-md focus:border-indigo-300 focus:bg-gray-50 focus:ring-2 focus:ring-indigo-100 outline-none resize-none p-2 transition-all overflow-hidden" // overflow-hidden 추가
                                                value={editableData.content}
                                                onChange={(e) => setEditableData({...editableData, content: e.target.value})}
                                            />
                                            {/* 수정 가능 힌트 아이콘 */}
                                            <div className="absolute top-2 right-2 text-gray-300 group-hover:text-indigo-400 pointer-events-none opacity-100 transition-opacity">
                                                <Pencil size={14} />
                                            </div>
                                        </div>

                                        {/* 2. 수정 가능한 버튼 영역 */}
                                        <div className="kakao_link_coupon">
                                            <div className="kakao_coupon">
                                                <div className="flex-align-center">
                                                    <div className="rounded-lg relative group w-max">
                                                        <input
                                                            type="text"
                                                            className="title w-[90px]"
                                                            maxLength={7}
                                                            value={editableData.couponTitle}
                                                            onChange={(e) => setEditableData({...editableData, couponTitle: e.target.value})}
                                                        />
                                                        <div className="absolute right-1 top-1/2 -translate-y-1/2 text-gray-400 group-hover:text-indigo-500 opacity-100 transition-opacity pointer-events-none">
                                                            <Pencil size={12} />
                                                        </div>
                                                    </div>
                                                    <div className="text-[12px]">무료 쿠폰</div>
                                                </div>
                                                <div className="desc">
                                                    <div className="rounded-lg relative group w-full">
                                                        <input
                                                            type="text"
                                                            className="title"
                                                            maxLength={12}
                                                            value={editableData.couponDescription}
                                                            onChange={(e) => setEditableData({...editableData, couponDescription: e.target.value})}
                                                        />
                                                        <div className="absolute right-0 top-1/2 -translate-y-1/2 text-gray-400 group-hover:text-indigo-500 opacity-100 transition-opacity pointer-events-none">
                                                            <Pencil size={12} />
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            <span className="kakao-icon icon_coupon"></span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="space-y-3 pt-4">
                                <button
                                    onClick={handleFinalSend}
                                    className="w-full bg-indigo-600 text-white py-4 rounded-xl font-bold text-lg shadow-lg hover:bg-indigo-700 transition-colors flex items-center justify-center"
                                >
                                    이대로 발송하기
                                </button>
                                <button
                                    onClick={() => window.location.href = `/volatility-message/edit?id=${result.messageId}`}
                                    className="w-full bg-gray-300 border-2 border-gray-200 py-3 rounded-xl font-bold hover:bg-gray-50 transition-colors"
                                >
                                    상세 수정하기
                                </button>
                                <button
                                    onClick={() => {
                                        if (window.confirm("다시 만드시겠습니까?")) {
                                            setStep(3)
                                        }
                                    }}
                                    className="w-full bg-white border-2 border-gray-200 text-gray-600 py-3 rounded-xl font-bold hover:bg-gray-50 transition-colors"
                                >
                                    다시 만들기
                                </button>
                            </div>
                        </div>
                    )}

                </div>
            </div>
        </div>
    );
};

export default AiMessageGenerator;